import { type FC, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Flex,
  Grid,
  Icon,
  Message,
  Spacing,
  Text,
  useTheme,
} from "@huub-web/ui";
import { useTranslation } from "react-i18next";
import { Illustration } from "@huub-web/shared";

import type { BaseSlideProps } from "../../../types";

import { slideContainerStyle } from "../AuthScreens.css";

export type EmailVerificationSlideProps = BaseSlideProps & {
  email: string | null;
  auth0Id: string | null;
  /**
   * Callback to resend the verification email.
   * Must be provided by the consuming app since the auth package has no API layer.
   */
  onResendVerificationEmail?: ({
    auth0Id,
  }: {
    auth0Id: string;
  }) => Promise<{ success: boolean }>;
  onResendSuccess?: () => void;
  onResendError?: () => void;
  onBackToLogin: () => void;
  onLoadingChange: (isLoading: boolean) => void;
};

/**
 * EmailVerificationSlide component for handling email verification.
 * Shows a message that the user needs to verify their email and allows resending.
 */
export const EmailVerificationSlide: FC<EmailVerificationSlideProps> = ({
  leading,
  trailing,
  email,
  auth0Id,
  onResendVerificationEmail,
  onResendSuccess,
  onResendError,
  onBackToLogin,
  onLoadingChange,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const hasIllustration = theme === "light" || theme === "dark";

  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [resendMessage, setResendMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;

    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [countdown]);

  const handleResendVerificationEmail = async () => {
    if (!canResend) {
      return;
    }

    if (!auth0Id) {
      console.warn("[EmailVerification] No auth0 ID available");
      return;
    }

    if (!onResendVerificationEmail) {
      console.warn(
        "[EmailVerification] No onResendVerificationEmail callback provided",
      );
      return;
    }

    setIsResending(true);
    setResendMessage(null);

    try {
      const result = await onResendVerificationEmail({ auth0Id });

      if (result?.success) {
        setResendMessage({
          type: "success",
          text: t("auth.resendVerificationEmail.successMessage"),
        });
        setCanResend(false);
        setCountdown(60);
        onResendSuccess?.();
      } else {
        throw new Error("Resend failed");
      }
    } catch (err) {
      console.error("[EmailVerification] Resend error:", err);
      setResendMessage({
        type: "error",
        text: t("auth.resendVerificationEmail.errorMessage"),
      });
      onResendError?.();
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    onLoadingChange?.(isResending);
  }, [isResending, onLoadingChange]);

  return (
    <>
      {leading}

      <Flex
        className={slideContainerStyle}
        direction="column"
        gap="m"
        align="center"
        justify="center"
      >
        <Flex direction="column" gap="s" align="center">
          {hasIllustration ? (
            <Illustration name="default" />
          ) : (
            <Spacing height="s" />
          )}

          <Text as="h2" variant="displayM" bold align="center">
            {t("auth.verifyEmail.heading")}
          </Text>
          <Text align="center" markdown>
            {t("auth.verifyEmail.message", {
              email: email ? `**${email}**` : "",
            })}
          </Text>
        </Flex>

        <Flex direction="column" gap="s" align="center" width="stretch">
          {resendMessage && (
            <Message
              variant={resendMessage.type === "success" ? "success" : "error"}
            >
              {resendMessage.text}
            </Message>
          )}

          <Grid variant="twoColumn" gap="xs">
            <Button
              variant="opaque"
              border="default"
              onClick={onBackToLogin}
              align="center"
            >
              {t("emailVerification.backToLogin")}
            </Button>
            <Button
              variant="secondary"
              onClick={() => void handleResendVerificationEmail()}
              disabled={!canResend || isResending}
              leading={<Icon icon="reload" size="s" />}
              align="center"
              trailing={
                countdown !== 0 && (
                  <Badge
                    inline
                    variant="tertiary"
                    value={t("auth.resendVerificationEmail.disabledMessage", {
                      seconds: countdown,
                      secondsLabel: countdown === 1 ? "sec" : "sec",
                    })}
                  />
                )
              }
            >
              {t("emailVerification.resendButton")}
            </Button>
          </Grid>
        </Flex>

        {trailing && (
          <>
            <Spacing height="m" />
            {trailing}
          </>
        )}
      </Flex>
    </>
  );
};
